<template>
  <div class="my-devices">
    <!-- 数据展示区域 -->
    <div class="data-section">
      <div class="section-header">
        <div>
          <span style="font-size: 14px; color: #333">用户管理</span>
        </div>
        <div class="toolbar">
          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData" class="custom-button-spacing">
            <el-icon><Refresh /></el-icon>
          </el-button>

          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '80px' ? 'primary' : ''"
                @click="setRowHeight('80px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '60px' ? 'primary' : ''"
                @click="setRowHeight('60px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '30px' ? 'primary' : ''"
                @click="setRowHeight('30px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text" class="custom-button-spacing">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover placement="bottom" width="100" v-model:visible="columnsDialogVisible">
            <div class="popover-content">
              <el-checkbox-group v-model="tempSelectedColumns">
                <el-checkbox
                  v-for="column in columns"
                  :key="column.prop"
                  :label="column.prop"
                >
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
              <div class="popover-footer">
                <el-button @click="resetColumns">重置</el-button>
                <el-button type="primary" @click="confirmColumns">确定</el-button>
              </div>
            </div>
            <template #reference>
              <el-button type="text" class="custom-button-spacing">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
          </el-popover>
        </div>
      </div>

      <!-- 表格区域 -->
      <el-table
        :data="tableData"
        style="width: 98.5%; margin-left: 10px; margin-right: 20px"
        :row-style="getRowStyle"
        :header-cell-style="{
        background: '#f5f7fa',
        color: '#909399',
        textAlign: 'center',
      }"
        :row-class-name="tableRowClassName"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :align="column.align || 'center'"
        >
          <template #default="scope">
            <template v-if="column.prop === 'operation'">
              <div class="operation-buttons">
                <el-button
                  type="danger"
                  size="small"
                  @click="handleDeleteUser(scope.row)"
                >
                  删除
                </el-button>
              </div>
            </template>
            <template v-else>
              {{ scope.row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
      
      <!-- 删除用户确认对话框 -->
      <el-dialog
        v-model="deleteDialogVisible"
        title="删除用户"
        width="30%"
      >
        <span>确定要删除该用户吗？此操作不可逆。</span>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="deleteDialogVisible = false">取消</el-button>
            <el-button type="danger" @click="confirmDeleteUser">确定</el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { Box, Refresh, Grid, Setting } from "@element-plus/icons-vue";
import { getUserList } from "@/api/user";
import { deleteMobileUser } from "@/api/webadmin";

export default {
  name: "UserManagePage",
  components: {
    Box,
    Refresh,
    Grid,
    Setting,
  },
  setup() {
    const tableData = ref([]);
    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);
    const deleteDialogVisible = ref(false);
    const currentUser = ref(null);
    const userRole = ref(1); // 用户角色

    // 表格配置
    const columns = ref([
      { prop: "index", label: "序号", visible: true },
      { prop: "account", label: "账户", visible: true },
      { prop: "username", label: "用户昵称", visible: true },
      { prop: "boundDevices", label: "绑定设备", visible: true },
      { prop: "sharedDevices", label: "分享设备", visible: true },
      { prop: "operation", label: "操作", visible: true },
    ]);

    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const tempSelectedColumns = ref([]);

    // 加载用户数据
    const loadData = async (isShow = true) => {
      try {
        const response = await getUserList();
        if(response.success){
          tableData.value = response.data.map((item, index) => ({
            id: item.id,
            index: index + 1,
            account: item.account,
            username: item.username,
            boundDevices: item.boundDevices.join(', ') || '无',
            sharedDevices: item.sharedDevices.join(', ') || '无'
         }));
        if(isShow) 
          ElMessage.success("数据已刷新");

      }
      } catch (error) {
        console.error('获取用户列表失败:', error);
        ElMessage.error('获取用户列表失败');
      }
    };
    
    // 处理删除用户
    const handleDeleteUser = (row) => {
      currentUser.value = row;
      deleteDialogVisible.value = true;
    };
    
    // 确认删除用户
    const confirmDeleteUser = async () => {
      try {
        await deleteMobileUser(currentUser.value.id);
        ElMessage.success('用户删除成功');
        deleteDialogVisible.value = false;
        await loadData(); // 重新加载数据
      } catch (error) {
        console.error('删除用户失败:', error);
        ElMessage.error('删除用户失败');
      }
    };

    // 表格样式
    const getRowStyle = () => {
      return { height: rowHeight.value };
    };

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    // 刷新数据
    const refreshData = () => {
      loadData();
    };

    // 设置表格行高
    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    // 重置列显示
    const resetColumns = () => {
      // 根据用户角色决定是否显示操作列
      if (userRole.value !== 0) {
        // 非管理员角色不显示操作列
        tempSelectedColumns.value = columns.value
          .filter(col => col.prop !== 'operation')
          .map(col => col.prop);
      } else {
        // 管理员显示所有列
        tempSelectedColumns.value = columns.value.map(col => col.prop);
      }
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    // 确认列显示设置
    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      
      // 非管理员角色强制移除操作列
      if (userRole.value !== 0) {
        const index = tempSelectedColumns.value.indexOf('operation');
        if (index > -1) {
          tempSelectedColumns.value.splice(index, 1);
        }
      }
      
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    onMounted(() => {
      // 初始化时获取用户角色并设置可见列
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userRole.value = user.role; // 0: 管理员, 1: 员工, 2: 维修工程师
        
        // 根据用户角色初始化selectedColumns
        if (userRole.value === 1 || userRole.value === 2) {
          selectedColumns.value = columns.value
            .filter(col => col.prop !== 'operation')
            .map(col => col.prop);
          tempSelectedColumns.value = [...selectedColumns.value];
        }
      }
      
      loadData(false);
      tempSelectedColumns.value = [...selectedColumns.value];
    });

    return {
      tableData,
      columns,
      visibleColumns,
      selectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      deleteDialogVisible,
      getRowStyle,
      tableRowClassName,
      refreshData,
      setRowHeight,
      resetColumns,
      confirmColumns,
      tempSelectedColumns,
      handleDeleteUser,
      confirmDeleteUser,
      userRole
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.toolbar {
  display: flex;
  gap: 8px;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 10px;
}

.density-button {
  display: flex;
  justify-content: center;
  margin: 10px;
}

.custom-button-spacing {
  margin-left: 8px;
}

.operation-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>